import classNames from 'classnames';

import './page.css';

type PageProps = {
  /**
   * The content to be rendered within the Page.
   */
  children: React.ReactNode;
  /**
   * Optional CSS class to apply to the Page container for custom styling.
   */
  className?: string;
  /**
   * Optional inline styles to apply to the Page container.
   */
  style?: React.CSSProperties;
  /**
   * The content type to apply to the Page container. Here are examples of when to use each type:
   * extra-light: Heavy text pages such as Home, Dev hub. Recommended for better readability
   * light: Management pages such as settings, billing, etc.
   * heavy: information dense pages such as Instance page, logs Ex.
   * Dashboard gallery, perspective gallery, data model gallery, projects
   * max: Full screen pages such as login, signup, forgot password, etc.
   * fluid: Full width pages such as graph tools, etc.
   */
  contentType?: 'extra-light' | 'light' | 'heavy' | 'max' | 'fluid';
  /**
   * Allows specifying the HTML element type for the Page container.
   * @default 'main'
   */
  as?: React.ElementType;
  'data-testid'?: string;
};

/**
 * This component is to be used when padding on the left, top, right and bottom sides of
 * the content is needed, which is dependent on breakpoints of the screen size. These paddings
 * will differ based on the `contentType` prop. This means for example overriding the style
 * prop with `padding: 0` should never occur.
 * For content that should take the full width and height of the screen without padding, use a
 * regular `div` instead.
 */
const Page = ({ children, className, style, contentType = 'light', as, ...restProps }: PageProps) => {
  const classes = classNames(
    `page-content`,
    {
      'page-extra-light': contentType === 'extra-light',
      'page-light': contentType === 'light',
      'page-heavy': contentType === 'heavy',
      'page-max': contentType === 'max',
      'page-fluid': contentType === 'fluid',
    },
    className,
  );

  const Component = as ?? 'main';

  return (
    <Component className={classes} style={style} {...restProps}>
      {children}
    </Component>
  );
};

export default Page;
